import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import { Icon } from "@fluentui/react/lib/Icon";
import { DataGrid, GridColDef, GridCellParams, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { muiTheme } from "system/styles/muiTheme";
import { useApi, useConfiguration, usePolicyEditorPermissions } from "system/hooks";
import usePageTitle from "system/hooks/use-page-title";
import { formatUtcToLocal, convertUtcDatesInText } from "system/libraries/utilities";
import { ConfirmationDialog, SuccessMessage, ErrorMessage } from "components/ui";
import TicketDetailsModal from "components/modules/policy-editor/components/mainPage/applicabilityRulesTab/TicketDetailsModal";
import { CompanyPolicyVersion } from "system/types/interfaces";
import { getAccountEmail } from "system/utils/getAccountEmail";
import {
  CompanyPolicyStatus,
  PolicyStatusLabels,
  PolicyStatusColors,
} from "components/modules/policy-editor/components/mainPage/persistenceTab/types";

const QuickFilterToolbar = () => (
  <Box sx={{ p: 1 }}>
    <GridToolbarQuickFilter
      debounceMs={300}
      placeholder="Search by comment, author, date..."
      sx={{
        width: "100%",
        "& .MuiInputBase-root": { color: "white" },
        "& .MuiSvgIcon-root": { color: "#8a8886" },
      }}
    />
  </Box>
);

export const PolicyVersionsPage = () => {
  const api = useApi();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const { currentTenant, isGlobalAdmin } = useConfiguration();
  usePageTitle({ title: "Policy Editor", subTitle: "Version History" });

  const { isReadOnly: readOnlyViewer, canDownload } = usePolicyEditorPermissions();

  const freshdeskApiUrl = process.env.REACT_APP_FRESHDESK_AZURE_FUNCTION_URL ?? "";

  const ticketData = useMemo(() => {
    const userName = accounts[0]?.name || "Aiden vision User";
    const userEmail = getAccountEmail(accounts[0]);
    const companyName = currentTenant?.siteName || "Unknown Company";
    const subject = `Customer Policy Update - ${companyName}`;
    return { userName, userEmail, companyName, subject };
  }, [accounts, currentTenant?.siteName]);

  const [versions, setVersions] = useState<CompanyPolicyVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorVisible, setErrorVisible] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [confirmImport, setConfirmImport] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CompanyPolicyVersion | null>(null);
  const [viewContent, setViewContent] = useState<{ version: number; policyText: string } | null>(null);
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteComment, setDeleteComment] = useState("");

  const [submitTarget, setSubmitTarget] = useState<CompanyPolicyVersion | null>(null);
  const [submittingId, setSubmittingId] = useState<string | null>(null);
  const [submitComment, setSubmitComment] = useState("");

  const [ticketTarget, setTicketTarget] = useState<CompanyPolicyVersion | null>(null);
  const [ticketPolicyText, setTicketPolicyText] = useState("");
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);

  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<HTMLElement | null>(null);
  const [downloadMenuRow, setDownloadMenuRow] = useState<CompanyPolicyVersion | null>(null);

  const hasDraft = useMemo(() => versions.some(v => v.status === CompanyPolicyStatus.Draft), [versions]);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setSuccessVisible(true);
  };

  const showError = (msg: string) => {
    setErrorMsg(msg);
    setErrorVisible(true);
  };

  const fetchVersions = useCallback(async () => {
    if (!currentTenant) return;
    setLoading(true);
    try {
      const response = await api.policyEditor.getPolicyVersions(currentTenant.siteId);
      setVersions(response?.items ?? []);
    } catch {
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [api.policyEditor, currentTenant]);

  const handleFirstVisit = useCallback(async () => {
    if (!currentTenant) return;
    setLoading(true);
    try {
      const response = await api.policyEditor.getPolicyVersions(currentTenant.siteId);
      const items = response?.items ?? [];

      if (items.length === 0) {
        const importResult = await api.policyEditor.importLatestCompanyIni(
          currentTenant.siteId,
          "Initial import from production"
        );
        if (!importResult?.isSuccess) {
          showError(importResult?.failMessage ?? "No Policy file was found for this company. Please verify that the company has been set up correctly in Blob Storage, or contact the Aiden team for assistance.");
          setLoading(false);
          return;
        }

        const seeded = await api.policyEditor.getPolicyVersions(currentTenant.siteId);
        const submittedId = seeded?.items?.[0]?.id;
        if (!submittedId) {
          showError("Import succeeded but could not find the new version.");
          setLoading(false);
          return;
        }

        const copyResult = await api.policyEditor.copyPolicyVersion(
          currentTenant.siteId,
          submittedId,
          "Initial draft"
        );
        if (copyResult?.isSuccess && copyResult.value) {
          navigate(`/${currentTenant.siteId}/policyeditor/edit/${copyResult.value.id}`);
          return;
        }
      }

      setVersions(items);
    } catch {
      showError("Failed to load versions.");
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [api.policyEditor, currentTenant, navigate]);

  useEffect(() => {
    handleFirstVisit();
  }, [handleFirstVisit]);

  const handleImport = async () => {
    if (!currentTenant) return;
    setConfirmImport(false);
    setImporting(true);
    try {
      const result = await api.policyEditor.importLatestCompanyIni(
        currentTenant.siteId,
        "Imported latest company.ini"
      );
      if (result?.isSuccess) {
        showSuccess("Policy file has been imported as a new version.");
        await fetchVersions();
      } else {
        showError(result?.failMessage ?? "Failed to import Policy file.");
      }
    } catch {
      showError("Failed to import Policy file");
    } finally {
      setImporting(false);
    }
  };

  const handleCopy = async (row: CompanyPolicyVersion) => {
    if (!currentTenant) return;
    setCopyingId(row.id);
    try {
      const result = await api.policyEditor.copyPolicyVersion(currentTenant.siteId, row.id);
      if (result?.isSuccess) {
        showSuccess(`Draft created from v${row.version}.`);
        await fetchVersions();
      } else {
        showError(result?.failMessage ?? "Failed to copy version");
      }
    } catch {
      showError("Failed to copy version");
    } finally {
      setCopyingId(null);
    }
  };

  const handleDelete = async () => {
    if (!currentTenant || !deleteTarget) return;
    const { id, version } = deleteTarget;
    const comment = deleteComment;
    setDeleteTarget(null);
    setDeleteComment("");
    setDeletingId(id);
    try {
      const result = await api.policyEditor.deletePolicyVersion(currentTenant.siteId, id, comment || undefined);
      if (result?.isSuccess) {
        showSuccess(`Version v${version} deleted.`);
        await fetchVersions();
      } else {
        showError(result?.failMessage ?? "Failed to delete version");
      }
    } catch {
      showError("Failed to delete version");
    } finally {
      setDeletingId(null);
    }
  };

  const handleView = async (row: CompanyPolicyVersion) => {
    if (!currentTenant) return;
    try {
      const result = await api.policyEditor.getPolicyVersion(currentTenant.siteId, row.id);
      if (result?.isSuccess && result.value) {
        setViewContent({ version: row.version, policyText: result.value.policyText });
      } else {
        showError(result?.failMessage ?? "Failed to load version content");
      }
    } catch {
      showError("Failed to load version content");
    }
  };

  const handleOpenVersion = (row: CompanyPolicyVersion) => {
    if (!currentTenant) return;
    if (row.status === CompanyPolicyStatus.Draft) {
      navigate(`/${currentTenant.siteId}/policyeditor/edit/${row.id}`);
    } else {
      handleView(row);
    }
  };

  const handleSubmitClick = async (row: CompanyPolicyVersion) => {
    if (canDownload) {
      setSubmitTarget(row);
    } else {
      setSubmittingId(row.id);
      try {
        if (!currentTenant) return;
        const result = await api.policyEditor.getPolicyVersion(currentTenant.siteId, row.id);
        if (result?.isSuccess && result.value) {
          setTicketPolicyText(result.value.policyText);
          setTicketTarget(row);
        } else {
          showError(result?.failMessage ?? "Failed to load version content");
        }
      } catch {
        showError("Failed to load version content");
      } finally {
        setSubmittingId(null);
      }
    }
  };

  const handleAdminSubmit = async () => {
    if (!currentTenant || !submitTarget) return;
    const { id, version } = submitTarget;
    const comment = submitComment;
    setSubmitTarget(null);
    setSubmitComment("");
    setSubmittingId(id);
    try {
      const result = await api.policyEditor.submitVersion(currentTenant.siteId, id, comment);
      if (result?.isSuccess) {
        showSuccess(`Version v${version} submitted.`);
        await fetchVersions();
      } else {
        showError(result?.failMessage ?? "Failed to submit version");
      }
    } catch {
      showError("Failed to submit version");
    } finally {
      setSubmittingId(null);
    }
  };

  const handleCustomerSubmit = async (subject: string, description: string) => {
    if (!currentTenant || !ticketTarget || !ticketPolicyText) return;
    setIsCreatingTicket(true);
    try {
      const changes = ticketTarget.pendingReplicateChanges ?? { additions: [], removals: [] };
      const additions = changes.additions ?? [];
      const removals = changes.removals ?? [];

      const escapeHtml = (s: string) =>
        s.replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");

      const sections: string[] = [];
      if (description.trim()) {
        sections.push(`<p>${escapeHtml(description).replace(/\n/g, "<br>")}</p>`);
      }
      if (additions.length > 0) {
        sections.push(
          `<h3 style="margin:16px 0 4px;">Add to Library (${additions.length})</h3><ul>` +
          additions.map((p) => `<li>${escapeHtml(p)}</li>`).join("") +
          `</ul>`
        );
      }
      if (removals.length > 0) {
        sections.push(
          `<h3 style="margin:16px 0 4px;">Remove from Library (${removals.length})</h3><ul>` +
          removals.map((p) => `<li>${escapeHtml(p)}</li>`).join("") +
          `</ul>`
        );
      }
      if (additions.length > 0 || removals.length > 0) {
        sections.push(
          `<p style="margin-top:16px;"><strong>Attached files:</strong></p><ul><li>Policy file (modified)</li></ul>`
        );
      }
      const finalDescription = sections.join("");

      const blob = new Blob([ticketPolicyText], { type: "text/plain" });
      const formData = new FormData();
      formData.append("name", ticketData.userName);
      formData.append("subject", subject);
      formData.append("description", finalDescription);
      formData.append("email", ticketData.userEmail);
      formData.append("status", "Open");
      formData.append("priority", "Low");
      formData.append("type", "Update Aiden config");
      formData.append("source", "1");
      formData.append("cfTypeOfRequest", "Change Policy (Company.ini) Request");
      formData.append("cfChangeType", "Other");
      formData.append("customerName", currentTenant.siteName ?? "");
      formData.append("attachments[]", blob, "Company.ini");

      const response = await fetch(freshdeskApiUrl, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const error = await response.text();
        showError(`Failed to create ticket: ${error}`);
        return;
      }

      const comment = `${subject}${description ? `\n${description}` : ""}`;
      const result = await api.policyEditor.submitVersion(
        currentTenant.siteId,
        ticketTarget.id,
        comment
      );

      if (result?.isSuccess) {
        showSuccess(`Version v${ticketTarget.version} submitted and ticket created.`);
        await fetchVersions();
      } else {
        showSuccess("Ticket created successfully. Version status update may be pending.");
      }
    } catch {
      showError("Failed to submit version");
    } finally {
      setIsCreatingTicket(false);
      setTicketTarget(null);
      setTicketPolicyText("");
    }
  };

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const extractBlobAndFilename = (result: unknown, fallback: string): { blob: Blob; filename: string } | null => {
    if (result instanceof Blob) {
      return { blob: result, filename: fallback };
    }
    if (result && typeof result === "object" && "data" in result && (result as { data: unknown }).data instanceof Blob) {
      const r = result as { data: Blob; filename?: string };
      return { blob: r.data, filename: r.filename || fallback };
    }
    return null;
  };

  const handleDownloadCompany = async (row: CompanyPolicyVersion) => {
    if (!currentTenant) return;
    setDownloadingId(row.id);
    try {
      const result = await api.policyEditor.downloadCompanyIni(currentTenant.siteId, row.id);
      const extracted = extractBlobAndFilename(result, `Company-v${row.version}.ini`);
      if (extracted) {
        triggerBlobDownload(extracted.blob, extracted.filename);
      } else {
        showError("Failed to download Policy file");
      }
    } catch {
      showError("Failed to download Policy file");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadReplicate = async (row: CompanyPolicyVersion) => {
    if (!currentTenant) return;
    setDownloadingId(row.id);
    try {
      const result = await api.policyEditor.downloadReplicateIni(currentTenant.siteId, row.id);
      const extracted = extractBlobAndFilename(result, `Replicate-v${row.version}.ini`);
      if (extracted) {
        triggerBlobDownload(extracted.blob, extracted.filename);
      } else {
        showError("Failed to download Library file");
      }
    } catch {
      showError("Failed to download Library file");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleOpenDownloadMenu = (event: React.MouseEvent<HTMLElement>, row: CompanyPolicyVersion) => {
    setDownloadMenuAnchor(event.currentTarget);
    setDownloadMenuRow(row);
  };

  const handleCloseDownloadMenu = () => {
    setDownloadMenuAnchor(null);
    setDownloadMenuRow(null);
  };

  const columns: GridColDef[] = [
    {
      field: "version",
      headerName: "Version",
      width: 120,
      renderCell: (params: GridCellParams) => {
        const row = params.row as CompanyPolicyVersion;
        const label = `v${params.value}`;
        if (row.status === CompanyPolicyStatus.Draft && row.copiedFromVersion) {
          return (
            <Box display="flex" alignItems="center" gap={0.5}>
              <span>{label}</span>
              <Chip label={`Based on v${row.copiedFromVersion}`} size="small" variant="outlined"
                sx={{ fontSize: "0.7rem", height: 20, color: "#a0a0a0", borderColor: "#606060" }} />
            </Box>
          );
        }
        return label;
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 230,
      renderCell: (params: GridCellParams) => {
        const row = params.row as CompanyPolicyVersion;
        const status = params.value as CompanyPolicyStatus;
        const additions = row.pendingReplicateChanges?.additions ?? [];
        const removals = row.pendingReplicateChanges?.removals ?? [];
        const totalChanges = additions.length + removals.length;
        return (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ height: "100%" }}>
            <Chip
              label={PolicyStatusLabels[status]}
              size="small"
              sx={{
                backgroundColor: PolicyStatusColors[status],
                color: "#ffffff",
              }}
            />
            {totalChanges > 0 && (
              <Tooltip
                arrow
                title={
                  <Box sx={{ whiteSpace: "pre-line" }}>
                    {[
                      additions.length > 0
                        ? `Add to Library (${additions.length}):\n` +
                          additions.slice(0, 6).map((p) => `  + ${p}`).join("\n") +
                          (additions.length > 6 ? `\n  +${additions.length - 6} more` : "")
                        : "",
                      removals.length > 0
                        ? `Remove from Library (${removals.length}):\n` +
                          removals.slice(0, 6).map((p) => `  - ${p}`).join("\n") +
                          (removals.length > 6 ? `\n  +${removals.length - 6} more` : "")
                        : "",
                    ].filter(Boolean).join("\n\n")}
                  </Box>
                }
              >
                <Chip
                  label={`±${totalChanges} library`}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(0, 120, 212, 0.15)",
                    color: "#4daafc",
                  }}
                />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: "comment",
      headerName: "Comment",
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const comment = params.value as string;
        if (!comment) return null;
        const converted = convertUtcDatesInText(comment);
        const lines = converted.split("\n---\n");
        const lastLine = lines[lines.length - 1];
        return (
          <Tooltip
            title={lines.length > 1 ? <span style={{ whiteSpace: "pre-line" }}>{converted}</span> : ""}
            arrow
            placement="bottom-start"
          >
            <Typography noWrap variant="body2" sx={{ color: "#F3F2F1", fontSize: "14px", lineHeight: "42px" }}>
              {lastLine}
            </Typography>
          </Tooltip>
        );
      },
    },
    { field: "createdBy", headerName: "Created By", width: 150 },
    { field: "updatedBy", headerName: "Modified By", width: 150 },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      width: 180,
      renderCell: (params: GridCellParams) => formatUtcToLocal(params.value as string),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 220,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const row = params.row as CompanyPolicyVersion;
        const isDeleted = row.status === CompanyPolicyStatus.Deleted;
        const canEdit = row.status === CompanyPolicyStatus.Draft;
        const canDelete = row.status === CompanyPolicyStatus.Draft || row.status === CompanyPolicyStatus.Inactive;
        const canSubmit = row.status === CompanyPolicyStatus.Draft;
        const canCopy = !isDeleted && !hasDraft;
        const isCopying = copyingId === row.id;
        const isDeleting = deletingId === row.id;
        const isSubmitting = submittingId === row.id;
        const isDownloading = downloadingId === row.id;

        return (
          <Box display="flex" gap={0.5} alignItems="center" height="100%">
            <Tooltip title={canEdit ? "Edit" : "View"} arrow>
              <IconButton size="small" sx={{ color: "#8a8886" }} onClick={() => handleOpenVersion(row)}>
                <Icon iconName={canEdit ? "Edit" : "View"} />
              </IconButton>
            </Tooltip>
            {canDownload && !isDeleted && (
              <Tooltip title="Download" arrow>
                <span>
                  <IconButton
                    size="small"
                    sx={{ color: "#8a8886" }}
                    disabled={isDownloading}
                    onClick={(e) => handleOpenDownloadMenu(e, row)}
                  >
                    {isDownloading ? <CircularProgress size={16} sx={{ color: "#8a8886" }} /> : <Icon iconName="Download" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!readOnlyViewer && !isDeleted && (
              <>
                {canCopy && (
                  <Tooltip title="Copy as Draft" arrow>
                    <span>
                      <IconButton size="small" sx={{ color: "#8a8886" }} disabled={isCopying} onClick={() => handleCopy(row)}>
                        {isCopying ? <CircularProgress size={16} sx={{ color: "#8a8886" }} /> : <Icon iconName="Copy" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {canSubmit && (
                  <Tooltip title="Submit" arrow>
                    <span>
                      <IconButton size="small" sx={{ color: "#4caf50" }} disabled={isSubmitting} onClick={() => handleSubmitClick(row)}>
                        {isSubmitting ? <CircularProgress size={16} sx={{ color: "#4caf50" }} /> : <Icon iconName="SkypeCircleCheck" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Delete" arrow>
                    <span>
                      <IconButton size="small" disabled={isDeleting} onClick={() => setDeleteTarget(row)}>
                        {isDeleting ? <CircularProgress size={16} sx={{ color: "#d5878b" }} /> : <Icon iconName="Delete" style={{ color: "#d5878b" }} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box mb={1} display="flex" justifyContent="flex-end" gap={1}>
        {!readOnlyViewer && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon iconName="Download" />}
            onClick={() => setConfirmImport(true)}
            disabled={importing}
            sx={{ textTransform: "none", color: "#F3F2F1", borderColor: "#605e5c", "&:hover": { borderColor: "#F3F2F1" } }}
          >
            {importing ? "Importing..." : "Import from production"}
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          onClick={() => fetchVersions()}
          disabled={loading}
          sx={{ minWidth: "auto", color: "#F3F2F1", borderColor: "#605e5c", "&:hover": { borderColor: "#F3F2F1" } }}
        >
          <Icon iconName="Refresh" />
        </Button>
      </Box>

      <ThemeProvider theme={muiTheme}>
        <div style={{ height: "640px", width: "100%" }}>
          <DataGrid
            rows={versions}
            columns={columns}
            loading={loading}
            slots={{ toolbar: QuickFilterToolbar }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
              sorting: {
                sortModel: [{ field: "version", sort: "desc" }],
              },
            }}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            rowHeight={42}
            columnHeaderHeight={42}
            sx={{
              border: "none",
              "& .MuiDataGrid-row": {
                backgroundColor: "#1b1a19",
                "&:hover": { backgroundColor: "#323130" },
                "&.Mui-selected": {
                  backgroundColor: "#3B3A39",
                  "&:hover": { backgroundColor: "#484644" },
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #414141",
                color: "#F3F2F1",
                fontSize: "14px",
                "&:focus, &:focus-within": { outline: "none" },
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#1b1a19",
                borderBottom: "1px solid #414141",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F3F2F1",
                fontSize: "12px",
                fontWeight: 700,
                "&:focus, &:focus-within": { outline: "none" },
              },
              "& .MuiDataGrid-columnSeparator": { color: "#414141" },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: "#1b1a19",
                borderTop: "1px solid #414141",
                color: "#F3F2F1",
                justifyContent: "center",
              },
              "& .MuiTablePagination-toolbar": {
                color: "#F3F2F1",
                alignItems: "baseline",
                paddingTop: "10px",
              },
              "& .MuiDataGrid-virtualScroller": {
                "&::-webkit-scrollbar": { width: "0.6rem" },
                "&::-webkit-scrollbar-thumb": { background: "#605e5c", borderRadius: "0.4rem" },
              },
            }}
          />
        </div>
      </ThemeProvider>

      <ConfirmationDialog
        isVisible={confirmImport}
        title="Import Policy"
        subText="This will import the latest Policy file from production as a new Submitted version. The current Submitted version will become Inactive. Continue?"
        onConfirm={handleImport}
        onCancel={() => setConfirmImport(false)}
        variant="success"
        confirmText="Import"
      />

      <ConfirmationDialog
        isVisible={!!deleteTarget}
        title="Delete Version"
        subText={`Are you sure you want to delete v${deleteTarget?.version}? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => { setDeleteTarget(null); setDeleteComment(""); }}
      >
        <TextField
          multiline
          minRows={2}
          maxRows={4}
          placeholder="Reason for deletion (optional)"
          value={deleteComment}
          onChange={(e) => setDeleteComment(e.target.value)}
          fullWidth
          size="small"
          sx={{
            mt: 1,
            "& .MuiInputBase-root": { color: "#F3F2F1", fontSize: "13px" },
            "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#605e5c" }, "&:hover fieldset": { borderColor: "#F3F2F1" } },
          }}
        />
      </ConfirmationDialog>

      <ConfirmationDialog
        isVisible={!!submitTarget}
        title="Submit Version"
        subText={`Submit v${submitTarget?.version}? This will replace the current Submitted version and become the new reference.`}
        onConfirm={handleAdminSubmit}
        onCancel={() => { setSubmitTarget(null); setSubmitComment(""); }}
        variant="success"
        confirmText="Submit"
        confirmDisabled={!submitComment.trim()}
      >
        <TextField
          autoFocus
          multiline
          minRows={2}
          maxRows={4}
          placeholder="Add a comment"
          value={submitComment}
          onChange={(e) => setSubmitComment(e.target.value)}
          fullWidth
          size="small"
          sx={{
            mt: 1,
            "& .MuiInputBase-root": { color: "#F3F2F1", fontSize: "13px" },
            "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#605e5c" }, "&:hover fieldset": { borderColor: "#F3F2F1" } },
          }}
        />
        {submitTarget && ((submitTarget.pendingReplicateChanges?.additions.length ?? 0) + (submitTarget.pendingReplicateChanges?.removals.length ?? 0)) > 0 && (
          <Box sx={{ mt: 1.5, p: 1.5, border: "1px solid #605e5c", borderRadius: 1, backgroundColor: "#2b2a29" }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Typography variant="subtitle2" sx={{ color: "#0078D4" }}>
                Library changes
              </Typography>
              <Chip
                label={(submitTarget.pendingReplicateChanges!.additions.length) + (submitTarget.pendingReplicateChanges!.removals.length)}
                size="small"
                sx={{ backgroundColor: "#0078D4", color: "#fff", height: 18, fontSize: "0.65rem" }}
              />
            </Box>
            {submitTarget.pendingReplicateChanges!.additions.length > 0 && (
              <Box sx={{ mb: submitTarget.pendingReplicateChanges!.removals.length > 0 ? 1 : 0 }}>
                <Typography variant="caption" sx={{ color: "#7fc97a", fontWeight: 600 }}>
                  Add ({submitTarget.pendingReplicateChanges!.additions.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#F3F2F1", maxHeight: 90, overflow: "auto" }}>
                  {submitTarget.pendingReplicateChanges!.additions.map((pkg) => (
                    <li key={`add-${pkg}`}><Typography variant="body2">{pkg}</Typography></li>
                  ))}
                </Box>
              </Box>
            )}
            {submitTarget.pendingReplicateChanges!.removals.length > 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: "#d5878b", fontWeight: 600 }}>
                  Remove ({submitTarget.pendingReplicateChanges!.removals.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#F3F2F1", maxHeight: 90, overflow: "auto" }}>
                  {submitTarget.pendingReplicateChanges!.removals.map((pkg) => (
                    <li key={`rm-${pkg}`}><Typography variant="body2">{pkg}</Typography></li>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </ConfirmationDialog>

      <TicketDetailsModal
        open={!!ticketTarget}
        onClose={() => { setTicketTarget(null); setTicketPolicyText(""); }}
        onSubmit={handleCustomerSubmit}
        isSubmitting={isCreatingTicket}
        initialSubject={ticketTarget ? `Policy update - v${ticketTarget.version} - ${ticketData.companyName}` : undefined}
        initialDescription={ticketTarget?.comment ?? ""}
        pendingReplicateChanges={ticketTarget?.pendingReplicateChanges ?? { additions: [], removals: [] }}
      />

      <Menu
        anchorEl={downloadMenuAnchor}
        open={!!downloadMenuAnchor}
        onClose={handleCloseDownloadMenu}
        PaperProps={{ sx: { backgroundColor: "#323130", color: "#F3F2F1" } }}
      >
        <MenuItem
          onClick={() => {
            if (downloadMenuRow) handleDownloadCompany(downloadMenuRow);
            handleCloseDownloadMenu();
          }}
        >
          <Icon iconName="Download" style={{ marginRight: 8 }} /> Download Policy
        </MenuItem>
        {isGlobalAdmin && (
          <MenuItem
            disabled={
              !downloadMenuRow ||
              ((downloadMenuRow.pendingReplicateChanges?.additions.length ?? 0) +
                (downloadMenuRow.pendingReplicateChanges?.removals.length ?? 0)) === 0
            }
            onClick={() => {
              if (downloadMenuRow) handleDownloadReplicate(downloadMenuRow);
              handleCloseDownloadMenu();
            }}
          >
            <Icon iconName="Download" style={{ marginRight: 8 }} /> Download Library
            {downloadMenuRow &&
              ((downloadMenuRow.pendingReplicateChanges?.additions.length ?? 0) +
                (downloadMenuRow.pendingReplicateChanges?.removals.length ?? 0)) > 0 && (
              <Chip
                label={`±${(downloadMenuRow.pendingReplicateChanges!.additions.length) + (downloadMenuRow.pendingReplicateChanges!.removals.length)}`}
                size="small"
                sx={{ ml: 1, backgroundColor: "rgba(0, 120, 212, 0.15)", color: "#4daafc", height: 18, fontSize: "0.65rem" }}
              />
            )}
          </MenuItem>
        )}
      </Menu>

      {/* View policytext modal */}
      <Dialog
        open={!!viewContent}
        onClose={() => setViewContent(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: "#323130", color: "white", minHeight: "60vh" },
        }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" component="span">Version v{viewContent?.version} - Policy Text</Typography>
          <IconButton onClick={() => setViewContent(null)} sx={{ color: "white" }}>
            <Icon iconName="Cancel" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            fullWidth
            value={viewContent?.policyText ?? ""}
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: "monospace",
                fontSize: "0.85rem",
                color: "white",
                backgroundColor: "#1e1e1e",
              },
            }}
            sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "#424242" } } }}
          />
        </DialogContent>
      </Dialog>

      <SuccessMessage
        isVisible={successVisible}
        onConfirm={() => setSuccessVisible(false)}
        title="Success"
        message={successMsg}
      />
      <ErrorMessage
        isVisible={errorVisible}
        onConfirm={() => setErrorVisible(false)}
        title="Something went wrong"
        message={errorMsg}
      />
    </>
  );
};
